<template>
  <div class="translation-container">
    <AppSectionHero
      :title="$t('pages.user.translation.title')"
      :subtitle="$t('pages.user.translation.subtitle')"
      :stats="heroStats"
      :show-refresh="false"
      @stat-click="selectRequestStatus"
    />

    <CCard class="border-0 shadow-sm filter-shell">
      <CCardBody class="p-3 p-lg-4">
        <div class="tabs-container">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="selectedTab = tab.id"
            :class="['tab-btn', { active: selectedTab === tab.id }]"
          >
            {{ tab.name }}
            <span v-if="tab.id === 'requests' && requests.length" class="tab-badge">
              {{ requests.length }}
            </span>
          </button>
        </div>
      </CCardBody>
    </CCard>

    <div v-if="loadError" class="example-banner">
      {{ loadError }}
    </div>

    <div v-if="selectedTab === 'requests'" class="tab-panel">
      <div class="panel-header">
        <h2 class="panel-title">Translation Queues</h2>
      </div>

      <div v-if="isLoading" class="empty-state">
        <div class="empty-icon">…</div>
        <h3 class="empty-title">Loading translation requests</h3>
        <p class="empty-message">Please wait while we gather your translation workspace.</p>
      </div>

      <div v-else-if="requests.length === 0" class="empty-state">
        <div class="empty-icon">✓</div>
        <h3 class="empty-title">No requests found</h3>
        <p class="empty-message">Create a translation request or adjust your search to see matching items.</p>
      </div>

      <div v-else-if="displayRequestSections.length" class="queue-sections">
        <section
          v-for="section in displayRequestSections"
          :key="section.id"
          class="queue-section"
        >
          <div class="queue-section__head">
            <div>
              <h3 class="queue-section__title">{{ section.title }}</h3>
            </div>
            <span class="queue-section__count">{{ section.items.length }}</span>
          </div>

          <div v-if="section.items.length" class="flagged-list">
            <div v-for="item in section.items" :key="item.id" class="flagged-item">
              <div class="item-header">
                <div class="item-badges">
                  <span class="flag-badge" :class="item.status">
                    {{ formatStatus(item.status) }}
                  </span>
                  <span class="language-badge">EN → {{ item.targetLanguageLabel }}</span>
                </div>
                <span class="item-date">{{ formatItemDate(item) }}</span>
              </div>

              <div class="document-title">{{ item.agreementTitle }}</div>
              <div class="document-meta">
                <span class="meta-pill">{{ item.agreementStage || 'Agreement' }}</span>
                <span class="meta-pill">Target {{ item.targetLanguageLabel }}</span>
                <span class="meta-pill">{{ formatTranslationMode(item.translationMode) }}</span>
              </div>

              <div class="document-workspace">
                <div class="preview-column">
                  <div class="preview-label">Request Notes</div>
                  <div class="preview-surface">
                    <p class="preview-text preview-document">{{ item.notes || 'No extra notes were provided for this translation request.' }}</p>
                  </div>
                </div>
                <div class="preview-column">
                  <div class="preview-label">{{ item.status === 'completed' ? `Translated Text (${item.targetLanguageLabel})` : 'Delivery Summary' }}</div>
                  <div class="preview-surface">
                    <p class="preview-text preview-document">{{ buildPreviewText(item) }}</p>
                  </div>
                  <div v-if="canDownloadResult(item)" class="download-actions">
                    <button
                      v-if="canDownloadResultDocx(item)"
                      class="download-link"
                      type="button"
                      @click="downloadResult(item, 'docx')"
                    >
                      Download DOCX
                    </button>
                    <button
                      v-if="canDownloadResultPdf(item)"
                      class="download-link"
                      type="button"
                      @click="downloadResult(item, 'pdf')"
                    >
                      Download PDF
                    </button>
                  </div>
                  <p v-if="item.status === 'completed' && item.translatorEngine" class="translation-engine">
                    Generated with {{ item.translatorEngine }}
                  </p>
                </div>
              </div>

              <div class="item-actions">
                <button class="action-btn secondary" type="button" @click="prefillFromRequest(item)">
                  Request Similar
                </button>
                <button class="action-btn primary" type="button" @click="selectedTab = 'new'">
                  New Request
                </button>
              </div>
            </div>
          </div>

          <div v-else class="queue-empty">
            <div class="queue-empty__title">{{ section.emptyTitle }}</div>
          </div>
        </section>
      </div>

      <div v-else class="empty-state">
        <div class="empty-icon">✓</div>
        <h3 class="empty-title">No translation requests in this view</h3>
      </div>
    </div>

    <div v-if="selectedTab === 'new'" class="tab-panel">
      <div class="panel-header">
        <h2 class="panel-title">Create Translation Request</h2>
      </div>

      <div class="request-shell">
        <div class="request-head">
          <h3>Translation Request Form</h3>
          <span class="muted">Choose whether to translate immediately with AI or send the request to admin for wording review.</span>
        </div>

        <div v-if="submitError" class="example-banner example-banner--error">
          {{ submitError }}
        </div>

        <div class="form-grid">
          <label class="field">
            <span>Translation Mode</span>
            <select v-model="form.translationMode">
              <option value="ai-self-service">Translate Now with AI</option>
              <option value="admin-review">Send to Admin Review</option>
            </select>
          </label>

          <label class="field">
            <span>Agreement</span>
            <select v-model="form.agreementId">
              <option value="">Standalone request</option>
              <option v-for="item in agreementOptions" :key="item.id" :value="String(item.id)">
                {{ item.title || 'Untitled agreement' }} - {{ item.partner || 'No partner' }}
              </option>
            </select>
          </label>

          <label class="field">
            <span>Target Language</span>
            <select v-model="form.targetLanguage">
              <option v-for="item in languageOptions" :key="item.value" :value="item.value">
                {{ item.label }}
              </option>
            </select>
          </label>

          <label class="field field--full">
            <span>Notes</span>
            <textarea
              v-model="form.notes"
              rows="5"
              placeholder="Explain which parts of the agreement should be translated, any deadline requirements, and any special instructions."
            />
          </label>

          <div class="mode-note field--full">
            {{ translationModeGuidance }}
          </div>
        </div>

        <div class="item-actions">
          <button class="action-btn secondary" type="button" @click="resetForm">
            Clear
          </button>
          <button class="action-btn primary" type="button" :disabled="isSubmitting" @click="createRequest">
            {{ isSubmitting ? (form.translationMode === 'ai-self-service' ? 'Translating...' : 'Submitting...') : (form.translationMode === 'ai-self-service' ? 'Translate with AI' : 'Submit Request') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="selectedTab === 'guide'" class="tab-panel">
      <div class="panel-header">
        <h2 class="panel-title">Translation Guidance</h2>
      </div>

      <div class="quality-dashboard">
        <div class="quality-stats">
          <div class="quality-card">
            <div class="quality-label">Before You Submit</div>
            <div class="quality-detail">Choose the correct agreement and make clear what sections need translation.</div>
          </div>
          <div class="quality-card">
            <div class="quality-label">Best Request Notes</div>
            <div class="quality-detail">Mention target audience, legal sensitivity, and whether the partner needs a formal or working translation.</div>
          </div>
          <div class="quality-card">
            <div class="quality-label">After Submission</div>
            <div class="quality-detail">Track status here and create a follow-up request only when the scope genuinely changes.</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { canAutoRefresh } from '@/projects/utils/refresh-guard'
import Service from '@/service/api'
import {
  downloadTextDocument,
  safeDocumentFileBase,
  triggerDocumentDownload
} from '@/utils/document-download'
import { readPageCache, writePageCache } from '@/utils/page-cache'
import { workflowNotificationEventName } from '@/utils/workflow-notifications'

const FALLBACK_LANGUAGE_OPTIONS = [
  { value: 'th', label: 'Thai' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'fr', label: 'French' }
]

const USER_TRANSLATION_CACHE_KEY = 'user-translation-page'
const USER_TRANSLATION_CACHE_MAX_AGE = 5 * 60 * 1000
const USER_TRANSLATION_REFRESH_INTERVAL_MS = 30000

function parseDateValue(value) {
  const timestamp = new Date(value || 0).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}

export default {
  name: 'UserTranslation',
  components: {
    AppSectionHero
  },
  data() {
    return {
      selectedTab: 'new',
      isLoading: false,
      isSubmitting: false,
      loadError: '',
      requests: [],
      activeRequestStatus: 'all',
      agreementOptions: [],
      languageOptions: FALLBACK_LANGUAGE_OPTIONS,
      tabs: [
        { id: 'new', name: 'New Request' },
        { id: 'requests', name: 'Queues' },
        { id: 'guide', name: 'Guidance' }
      ],
      form: {
        agreementId: '',
        translationMode: 'ai-self-service',
        targetLanguage: 'th',
        notes: ''
      },
      refreshTimer: null,
      submitError: ''
    }
  },
  computed: {
    filteredRequests() {
      return this.requests.filter(item => this.matchesRequestStatus(item))
    },
    requestQueueItems() {
      return this.filteredRequests
        .filter(item => String(item && item.status ? item.status : '') !== 'completed')
        .slice()
        .sort((left, right) => parseDateValue(right && right.requestedAt) - parseDateValue(left && left.requestedAt))
    },
    translatedQueueItems() {
      return this.filteredRequests
        .filter(item => String(item && item.status ? item.status : '') === 'completed')
        .slice()
        .sort((left, right) => {
          const completedDiff = parseDateValue(right && right.completedAt) - parseDateValue(left && left.completedAt)
          if (completedDiff !== 0) return completedDiff
          return parseDateValue(right && right.requestedAt) - parseDateValue(left && left.requestedAt)
        })
    },
    requestSections() {
      const sections = []

      if (this.activeRequestStatus !== 'completed') {
        sections.push({
          id: 'request-queue',
          title: 'Active Requests',
          emptyTitle: 'No active requests',
          items: this.requestQueueItems
        })
      }

      if (this.activeRequestStatus !== 'pending' && this.activeRequestStatus !== 'in-progress') {
        sections.push({
          id: 'translated-queue',
          title: 'Completed Translations',
          emptyTitle: 'No completed translations',
          items: this.translatedQueueItems
        })
      }

      return sections
    },
    displayRequestSections() {
      if (this.activeRequestStatus !== 'all') return this.requestSections
      return this.requestSections.filter(section => section.items.length)
    },
    heroStats() {
      return [
        {
          label: 'Pending',
          value: this.requests.filter(item => item.status === 'pending').length,
          icon: 'cil-clock',
          iconClass: 'app-section-stat__icon--attention',
          clickable: true,
          active: this.activeRequestStatus === 'pending'
        },
        {
          label: 'In Progress',
          value: this.requests.filter(item => item.status === 'in-progress').length,
          icon: 'cil-loop',
          iconClass: 'app-section-stat__icon--total',
          clickable: true,
          active: this.activeRequestStatus === 'in-progress'
        },
        {
          label: 'Completed',
          value: this.requests.filter(item => item.status === 'completed').length,
          icon: 'cil-check-circle',
          iconClass: 'app-section-stat__icon--active',
          clickable: true,
          active: this.activeRequestStatus === 'completed'
        }
      ]
    },
    translationModeGuidance() {
      if (this.form.translationMode === 'ai-self-service') {
        return 'AI translation works best when you choose an agreement that already contains draft text. The result will appear here immediately and can still be checked later if needed.'
      }
      return 'Admin review sends the request into the existing translation workflow so the wording can be checked before final delivery.'
    }
  },
  watch: {
    '$route.fullPath'() {
      this.applyRouteFilters()
    }
  },
  mounted() {
    this.loadCachedPage()
    this.applyRouteFilters()
    this.loadPage({ forceRefresh: true })
    this.refreshTimer = window.setInterval(() => {
      if (canAutoRefresh()) this.loadPage({ forceRefresh: true, silent: true })
    }, USER_TRANSLATION_REFRESH_INTERVAL_MS)
    window.addEventListener('focus', this.handleWindowFocus)
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
    window.removeEventListener('focus', this.handleWindowFocus)
  },
  methods: {
    loadCachedPage() {
      const cached = readPageCache(USER_TRANSLATION_CACHE_KEY, USER_TRANSLATION_CACHE_MAX_AGE)
      if (!cached || typeof cached !== 'object') return
      this.requests = Array.isArray(cached.requests) ? cached.requests : []
      this.agreementOptions = Array.isArray(cached.agreementOptions) ? cached.agreementOptions : []
      this.languageOptions = Array.isArray(cached.languageOptions) && cached.languageOptions.length
        ? cached.languageOptions
        : this.languageOptions
    },
    applyRouteFilters() {
      const query = this.$route && this.$route.query ? this.$route.query : {}
      const tab = String(query.tab || '').trim().toLowerCase()
      if (['new', 'requests', 'guide'].includes(tab)) {
        this.selectedTab = tab
      }
      const status = String(query.status || '').trim().toLowerCase()
      if (['all', 'pending', 'in-progress', 'completed', 'rejected'].includes(status)) {
        this.activeRequestStatus = status
        this.selectedTab = 'requests'
      }
    },
    handleWindowFocus() {
      this.loadPage({ forceRefresh: true, silent: true })
    },
    notifyWorkflowChange() {
      window.dispatchEvent(new CustomEvent(workflowNotificationEventName(), {
        detail: { source: 'user-translation' }
      }))
    },
    matchesRequestStatus(item) {
      if (this.activeRequestStatus === 'all') return true
      return String(item && item.status ? item.status : '') === this.activeRequestStatus
    },
    selectRequestStatus(stat) {
      const label = String(stat && stat.label ? stat.label : '').toLowerCase()
      const nextStatus = {
        pending: 'pending',
        'in progress': 'in-progress',
        completed: 'completed'
      }[label] || 'all'
      this.activeRequestStatus = this.activeRequestStatus === nextStatus ? 'all' : nextStatus
      this.selectedTab = 'requests'
    },
    async loadPage(options = {}) {
      const silent = !!options.silent
      const forceRefresh = !!options.forceRefresh
      if (!silent) {
        this.isLoading = true
        this.loadError = ''
      }

      try {
        await this.$store.dispatch('auth/bootstrapSession')
        const hasToken = !!(this.$store && this.$store.state && this.$store.state.XAccessToken)
        const isAuthenticated = !!(this.$store && this.$store.getters && this.$store.getters['auth/authenticated'] && this.$store.getters['auth/authenticated'].isAuthen)
        if (!hasToken || !isAuthenticated) {
          if (!silent) {
            this.loadError = 'Your session has expired. Please sign in again.'
          }
          return
        }

        const shouldLoadStaticOptions = !silent || !this.agreementOptions.length || !this.languageOptions.length
        const [requests, agreements, languages] = await Promise.allSettled([
          Service.userWorkspace('translation-requests', {}, { forceRefresh }),
          shouldLoadStaticOptions ? Service.agreements('list') : Promise.resolve({ data: { data: this.agreementOptions } }),
          shouldLoadStaticOptions ? Service.userWorkspace('translation-language-options') : Promise.resolve({ data: { data: this.languageOptions } })
        ])

        if (requests.status === 'fulfilled' && requests.value && requests.value.data && requests.value.data.data) {
          this.requests = requests.value.data.data
        } else if (!silent) {
          this.requests = []
        }
        if (agreements.status === 'fulfilled' && agreements.value && agreements.value.data && agreements.value.data.data) {
          this.agreementOptions = agreements.value.data.data
        } else if (!silent) {
          this.agreementOptions = []
        }
        if (languages.status === 'fulfilled' && languages.value && languages.value.data && languages.value.data.data) {
          this.languageOptions = languages.value.data.data
        } else if (!silent) {
          this.languageOptions = FALLBACK_LANGUAGE_OPTIONS
        }

        if ([requests, agreements, languages].every(item => item.status !== 'fulfilled')) {
          this.loadError = 'The translation page could not load right now. Please refresh and try again.'
        } else if ([requests, agreements, languages].some(item => item.status !== 'fulfilled')) {
          if (!silent) {
            this.loadError = 'Some translation data could not be loaded, but the page is still available.'
          }
        } else {
          this.loadError = ''
        }

        writePageCache(USER_TRANSLATION_CACHE_KEY, {
          requests: this.requests,
          agreementOptions: this.agreementOptions,
          languageOptions: this.languageOptions
        })
      } catch (error) {
        if (!silent) {
          this.loadError = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
            ? error.response.data.data.message
            : 'The translation page could not load right now. Please refresh and try again.'
        }
      } finally {
        if (!silent) {
          this.isLoading = false
        }
      }
    },
    async createRequest() {
      this.isSubmitting = true
      this.submitError = ''
      try {
        await Service.userWorkspace('create-translation-request', this.form)
        this.resetForm()
        this.selectedTab = 'requests'
        await this.loadPage({ forceRefresh: true })
        this.notifyWorkflowChange()
      } catch (error) {
        this.submitError = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
          ? error.response.data.data.message
          : 'Translation request could not be submitted right now.'
      } finally {
        this.isSubmitting = false
      }
    },
    resetForm() {
      this.form = {
        agreementId: '',
        translationMode: 'ai-self-service',
        targetLanguage: 'th',
        notes: ''
      }
      this.submitError = ''
    },
    prefillFromRequest(item) {
      this.form = {
        agreementId: item && item.agreementId ? String(item.agreementId) : '',
        translationMode: item && item.translationMode ? item.translationMode : 'ai-self-service',
        targetLanguage: item && item.targetLanguage ? item.targetLanguage : 'th',
        notes: item && item.notes ? item.notes : ''
      }
      this.submitError = ''
      this.selectedTab = 'new'
    },
    formatTranslationMode(value) {
      return value === 'ai-self-service' ? 'AI Self-Translate' : 'Admin Review'
    },
    formatStatus(value) {
      return {
        pending: 'Pending',
        'in-progress': 'In Progress',
        completed: 'Completed',
        rejected: 'Returned'
      }[value] || 'Pending'
    },
    formatItemDate(item) {
      if (item && item.status === 'completed') {
        return `Completed ${this.formatDate(item.completedAt || item.requestedAt)}`
      }
      return `Requested ${this.formatDate(item && item.requestedAt)}`
    },
    formatDate(value) {
      return new Date(value || new Date()).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },
    buildDeliverySummary(item) {
      if (!item) return 'No delivery details yet.'
      if (item.status === 'completed') {
        return `Completed via ${this.formatTranslationMode(item.translationMode).toLowerCase()}${item.completedAt ? ` on ${this.formatDate(item.completedAt)}` : ''}.`
      }
      if (item.status === 'in-progress') {
        return 'This request is currently being processed by the translation workflow.'
      }
      if (item.translationMode === 'admin-review') {
        return 'This request is waiting for admin review and translation handling.'
      }
      return 'This request is waiting to be processed.'
    },
    buildPreviewText(item) {
      if (item && item.status === 'completed') {
        return item.translatedPreviewText || item.translatedText || this.buildDeliverySummary(item)
      }
      return this.buildDeliverySummary(item)
    },
    canDownloadResult(item) {
      return this.canDownloadResultDocx(item) || this.canDownloadResultPdf(item)
    },
    hasTranslatedResultText(item) {
      return !!String(item && (item.translatedPreviewText || item.translatedText) ? item.translatedPreviewText || item.translatedText : '').trim()
    },
    canDownloadResultDocx(item) {
      return !!(item && item.status === 'completed' && (item.outputUrl || this.hasTranslatedResultText(item)))
    },
    canDownloadResultPdf(item) {
      return !!(item && item.status === 'completed' && (this.hasTranslatedResultText(item) || (item.outputUrl && this.isPdfOutput(item))))
    },
    isPdfOutput(item) {
      const outputName = String(item && item.outputName || '').toLowerCase()
      const outputUrl = String(item && item.outputUrl || '').toLowerCase()
      return outputName.endsWith('.pdf') || outputUrl.startsWith('data:application/pdf')
    },
    async refreshTranslationResult(item) {
      await this.loadPage({ forceRefresh: true, silent: true })
      return this.requests.find(entry => String(entry.id) === String(item && item.id)) || item
    },
    resultFileBase(item) {
      return `${safeDocumentFileBase(item && item.agreementTitle ? item.agreementTitle : 'translated-document')}-${safeDocumentFileBase(item && item.targetLanguage ? item.targetLanguage : 'translated')}`
    },
    async downloadResult(item, format = 'docx') {
      if (format === 'pdf' && !this.canDownloadResultPdf(item)) return
      if (format !== 'pdf' && !this.canDownloadResultDocx(item)) return
      let activeItem = item
      if (!this.hasTranslatedResultText(activeItem) || !activeItem.outputUrl) {
        activeItem = await this.refreshTranslationResult(item)
      }
      if (this.hasTranslatedResultText(activeItem)) {
        return downloadTextDocument({
          title: activeItem.agreementTitle || 'Translated Document',
          subtitle: activeItem.targetLanguageLabel ? `Translated to ${activeItem.targetLanguageLabel}` : '',
          bodyText: this.buildPreviewText(activeItem),
          fileBase: this.resultFileBase(activeItem),
          activityType: activeItem.agreementStage,
          partner: activeItem.partner || '',
          country: activeItem.country || '',
          partnerLogo: activeItem.partnerLogo || null,
          format
        })
      }
      if (activeItem.outputUrl && (format !== 'pdf' || this.isPdfOutput(activeItem))) {
        return triggerDocumentDownload(
          activeItem.outputUrl,
          activeItem.outputName || `translation-${activeItem.id || 'document'}.${format === 'pdf' ? 'pdf' : 'docx'}`
        )
      }
      return false
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("translation-container");
@include workspace-toolbar-search;
@include workspace-button-system;

.translation-container {
  margin: 0 auto;
  max-width: 1400px;
  padding: 32px;
}

.tabs-container {
  display: flex;
  gap: 8px;
}

.tab-btn {
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;
  position: relative;
  transition: all 0.2s;
}

.tab-btn.active {
  color: #9e212f;
  font-weight: 600;
}

.tab-btn.active::after {
  background: #9e212f;
  bottom: -1px;
  content: '';
  height: 2px;
  left: 0;
  position: absolute;
  right: 0;
}

.tab-badge {
  background: #fee2e2;
  border-radius: 12px;
  color: #b91c1c;
  font-size: 11px;
  margin-left: 6px;
  padding: 2px 6px;
}

.example-banner {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  color: #9a3412;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 20px;
  padding: 12px 16px;
}

.example-banner--error {
  background: #fff1f2;
  border-color: #fecdd3;
  color: #be123c;
}

.tab-panel {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #e7edf4;
  border-radius: 22px;
  box-shadow: 0 18px 38px rgba(44, 52, 71, 0.08);
  overflow: hidden;
  padding: 24px;
}

.panel-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.panel-title,
.section-title {
  color: #0f172a;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.queue-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.queue-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.queue-section__head {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.queue-section__title {
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}

.queue-section__copy {
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
  margin: 6px 0 0;
  max-width: 720px;
}

.queue-section__count {
  background: #f8fafc;
  border: 1px solid #dbe5f0;
  border-radius: 999px;
  color: #344767;
  font-size: 12px;
  font-weight: 700;
  min-width: 36px;
  padding: 6px 12px;
  text-align: center;
}

.queue-empty {
  background: #f8fafc;
  border: 1px dashed #dbe5f0;
  border-radius: 16px;
  padding: 20px;
}

.queue-empty__title {
  color: #0f172a;
  font-size: 15px;
  font-weight: 700;
}

.queue-empty__copy {
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
  margin-top: 6px;
}

.panel-actions {
  display: flex;
  gap: 12px;
}

.search-box.full-width {
  margin-bottom: 20px;
  width: 100%;
}

.search-box input:focus,
.form-input:focus,
.form-select:focus,
.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
}

.flagged-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.flagged-item,
.request-shell {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  transition: all 0.2s;
}

.flagged-item:hover {
  border-color: #9e212f;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.item-header,
.request-head {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
}

.item-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.flag-badge,
.language-badge {
  border-radius: 30px;
  display: inline-block;
  font-size: 12px;
  padding: 4px 12px;
}

.flag-badge.pending {
  background: #fef9c3;
  color: #854d0e;
}

.flag-badge.in-progress {
  background: #f7e8eb;
  color: #8a1728;
}

.flag-badge.completed {
  background: #dcfce7;
  color: #166534;
}

.language-badge {
  background: #f1f5f9;
  color: #334155;
}

.item-date,
.muted {
  color: #64748b;
  font-size: 12px;
}

.document-title {
  color: #0f172a;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
}

.document-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.meta-pill {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 999px;
  color: #9a3412;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
}

.document-workspace {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  margin: 16px 0;
  padding: 16px;
  width: 100%;
}

.preview-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.preview-surface {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  min-height: 180px;
  min-width: 0;
  overflow: hidden;
  padding: 16px;
}

.preview-label {
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.preview-text {
  color: #0f172a;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.preview-document {
  white-space: pre-wrap;
}

.download-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.download-link {
  align-self: flex-start;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 999px;
  color: #9a3412;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  margin-top: 0;
  padding: 8px 14px;
}

.translation-engine,
.mode-note {
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
}

.mode-note {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
}

.item-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.action-btn {
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 16px;
  transition: all 0.2s;
}

.action-btn.primary {
  background: #9e212f;
  color: white;
}

.action-btn.primary:hover {
  background: #8a1728;
}

.action-btn.secondary {
  background: white;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.action-btn.secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.action-btn:disabled {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 1;
}

.form-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 16px;
}

.field {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.field span {
  color: #344767;
  font-weight: 700;
}

.field input,
.field select,
.field textarea {
  border: 1px solid #dde4ec;
  border-radius: 12px;
  padding: 12px;
  width: 100%;
  max-width: 100%;
}

.field--full {
  grid-column: 1 / -1;
}

.quality-dashboard {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.quality-stats {
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(3, 1fr);
}

.quality-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
}

.quality-label {
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
}

.quality-detail {
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.empty-state {
  color: #64748b;
  padding: 40px;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-title {
  color: #0f172a;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.empty-message {
  margin: 0;
}

@media (max-width: 1024px) {
  .translation-container {
    padding: 24px;
  }

  .quality-stats {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .translation-container {
    padding: 20px;
  }

  .queue-section__head,
  .panel-header,
  .item-header,
  .item-actions,
  .request-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .document-workspace,
  .quality-stats,
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
